// react
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
// store
import { googleMapsAPIKEY } from "../../store/config";
import { setSearchConfig } from "../../store/actions";
import { searchService } from "../../store/services/ServiceSearch";
import { trackingService } from "../../store/services/ServiceTracking";
import Geocode from "react-geocode";
import { buildGeo, geoFromAddressComponents } from "../../store/helpers/skRegions";
import {
    GoogleMap,
    LoadScript,
    Marker,
    InfoWindow,
    DirectionsService,
    DirectionsRenderer,
} from "@react-google-maps/api";
import SearchMapOverlay from "./SearchMapOverlay";
import InfoWindowView from "./InfoWindowView";
import Loading from "../../components/others/Loading";
import moment from "moment";
import uuid from "react-uuid";
import "moment/min/locales.min";
import { GAPageView, initGA } from "../../index";

Geocode.setApiKey(googleMapsAPIKEY);
moment.locale("sk");

const mapStyles = {
    height: "100vh",
    width: "100%",
};

const libraries = ["places", "geometry", "drawing"];

const defaultOptions = {
    zoomControl: false,
    mapTypeControl: false,
    scaleControl: false,
    streetViewControl: false,
    rotateControl: false,
    fullscreenControl: false,
    styles: [
        {
            featureType: "poi",
            elementType: "labels.icon",
            stylers: [
                {
                    visibility: "off",
                },
            ],
        },
        {
            featureType: "poi.business",
            stylers: [{ visibility: "off" }],
        },
        {
            featureType: "transit",
            elementType: "labels.icon",
            stylers: [{ visibility: "off" }],
        },
    ],
};
const marker = {
    url: "/images/icons/AP_Map_Pointer.svg",
    scaledSize: { width: 70, height: 70 },
    anchor: { x: 35, y: 35 },
};
const markerActivities = {
    url: "/images/icons/icon_map_activities.svg",
    scaledSize: { width: 35, height: 35 },
    anchor: { x: 17.5, y: 17.5 },
};

// component
function Search() {
    // redux stuff
    const searchData = useSelector((state) => state?.search);
    let dispatch = useDispatch();

    // helper data holding states
    const [response, setResponse] = useState(null);
    const [loadConfigSettings, setLoadConfigSettings] = useState(false);

    // search config
    const [activityTypes, setActivityTypes] = useState(searchData.activities);

    // states related to positions
    const [currentPosition, setCurrentPosition] = useState({
        lat: searchData.location.latitude,
        lng: searchData.location.longitude,
    });
    const placeGeoRef = useRef({ source: null });

    // states related to activities
    const [selectedAddress, setSelectedAddress] = useState({});
    const [foundActivities, setFoundActivities] = useState([]);
    const [visibleEvents, setVisibleEvents] = useState([]);
    const [fallbackInfo, setFallbackInfo] = useState(null);

    // states related to filters
    const [distance, setDistance] = useState(searchData.distance || 10);
    const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
    const [time, setTime] = useState(moment().format("HH:00"));

    // states related to UI
    const [searching, setSearching] = useState(false);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [locationReady, setLocationReady] = useState(false);
    const [locationSetManually, setLocationSetManually] = useState(
        searchData.locationSetManually
    );

    // helper functions to set the current position
    const initCurrentPosition = useCallback(() => {
        const fallback = () => setLocationReady(true);
        const options = { timeout: 8000, maximumAge: 60000 };

        if (!("geolocation" in navigator)) {
            fallback();
            return;
        }

        if (navigator.permissions && navigator.permissions?.query) {
            navigator.permissions
                .query({ name: "geolocation" })
                .then((result) => {
                    if (["granted", "prompt"].includes(result.state)) {
                        navigator.geolocation.getCurrentPosition(
                            getCurrentPositionCallback,
                            fallback,
                            options
                        );
                    } else {
                        fallback();
                    }
                })
                .catch(fallback);
        } else {
            navigator.geolocation.getCurrentPosition(
                getCurrentPositionCallback,
                fallback,
                options
            );
        }
    }, []);

    const resolvePlaceGeo = async (lat, lng, source) => {
        const current = placeGeoRef.current || {};
        const hasCity = !!(current.city && current.lat === lat && current.lng === lng);
        if (hasCity) return current;
        if (current.city && current.source === "autocomplete") {
            return { ...current, lat, lng };
        }
        try {
            const response = await Geocode.fromLatLng(lat, lng);
            const components = response?.results?.[0]?.address_components || [];
            const formattedAddress = response?.results?.[0]?.formatted_address || "";
            const resolved = {
                source: source || current.source || "geocode",
                lat,
                lng,
                ...geoFromAddressComponents(components, formattedAddress),
            };
            placeGeoRef.current = resolved;
            return resolved;
        } catch (e) {
            const fallback = { ...current, source: source || current.source || "geocode", lat, lng };
            placeGeoRef.current = fallback;
            return fallback;
        }
    };

    const getCurrentPositionCallback = (position) => {
        const coords = position?.coords;
        const { latitude, longitude } = coords;

        if (window?.document?.querySelector("#map-search-bar")?.length > 0) {
            window.document.querySelector("#map-search-bar").value = "";
        }

        placeGeoRef.current = { source: "gps", lat: latitude, lng: longitude };

        setCurrentPosition({
            lat: latitude,
            lng: longitude,
        });
        setLocationSetManually(false);
        setLocationReady(true);
    };

    useEffect(() => {
        initGA();
        GAPageView();
    }, []);

    useEffect(() => {
        // if the user already has a manually defined location - use it directly,
        // otherwise request GPS and wait for it (initial search waits for locationReady)
        if (locationSetManually) {
            setLocationReady(true);
        } else {
            initCurrentPosition();
        }
        // eslint-disable-next-line
    }, [initCurrentPosition]);

    // function to handle searching - event should be null by default since we want to use this method
    // not onClick only
    // location should be also null since we want to search for the currentPosition initially
    const handleSearch = async (event = null, location = null) => {
        if (!mapLoaded) return;

        // if we don't have an event - don't call it - we are avoiding a potential error here
        if (event) event.preventDefault();

        // batch setStates - reset
        setResponse(null);
        setFoundActivities([]);
        setVisibleEvents([]);
        setFallbackInfo(null);
        setSearching(true);
        setLoadConfigSettings(false);

        // set new search config to the redux store
        // TODO - Delete date and time saving ... unnecessary feature
        const data = {
            location: {
                latitude: parseFloat(location?.lat || currentPosition.lat),
                longitude: parseFloat(location?.lng || currentPosition.lng),
            },
            distance: distance,
            date: date,
            time: time,
            country: "SK",
            activities: activityTypes,
            locationSetManually: locationSetManually,
        };

        dispatch(setSearchConfig(data));

        const response = await searchService.search(data);
        const activities = Array.isArray(response) ? response : (response?.results || []);
        const fallbackUsed = !Array.isArray(response) && !!response?.fallback_used;
        const effectiveRange = !Array.isArray(response) ? response?.effective_range || null : null;
        const newActivities = packDuplicates(activities);

        // end searching, set found activities and batch setStates
        setFoundActivities(newActivities);
        setVisibleEvents(newActivities);
        setFallbackInfo(fallbackUsed ? { range: effectiveRange } : null);
        setSearching(false);

        const resultCount = activities.length;
        const placeGeo = await resolvePlaceGeo(
            data.location.latitude,
            data.location.longitude,
            placeGeoRef.current?.source
        );
        const geo = buildGeo({
            regionName: placeGeo.regionName,
            district: placeGeo.district,
            city: placeGeo.city,
            postalCode: placeGeo.postalCode,
            lat: data.location.latitude,
            lng: data.location.longitude,
            source: placeGeo.source,
        });
        const basePayload = {
            geo: geo,
            region: geo.region,
            distance_km: data.distance ? Math.round(data.distance / 1000) : null,
            date: data.date || null,
            time_from: data.time || null,
            time_to: null,
            activity_type_ids: Array.isArray(activityTypes)
                ? activityTypes.map((item) => (item && typeof item === "object" ? item.id ?? item.value : item)).filter((id) => id != null)
                : [],
        };

        if (fallbackUsed) {
            // requested date had no results -> keep the unmet-demand signal
            trackingService.trackSearch(
                { ...basePayload, result_count: 0, fallback_used: true, fallback_result_count: resultCount },
                false
            );
            // results actually shown come from the 7-day fallback -> clicks attribute here
            trackingService.trackSearch(
                { ...basePayload, result_count: resultCount, is_fallback: true, effective_range: effectiveRange },
                true
            );
        } else {
            trackingService.trackSearch(
                { ...basePayload, result_count: resultCount },
                resultCount > 0
            );
        }
    };

    // search on first map load (once location is resolved) and whenever the position changes
    useEffect(() => {
        if (!mapLoaded || !locationReady) return;

        const search = async () => {
            await handleSearch(null, currentPosition);
        };

        search();

        // eslint-disable-next-line
    }, [currentPosition, mapLoaded, locationReady]);

    const packDuplicates = (activities) => {
        let packedActivites = [];
        let alreadyPacked = [];

        let remainingActivities = [...activities];
        for (let i = 0; i < activities.length; i++) {
            let current = activities[i];
            if (!alreadyPacked.includes(current.id)) {
                const matched = remainingActivities.filter(
                    (activity) =>
                        compareAddressLocation(current, activity) &&
                        activity.id !== current.id
                );
                let packedList =
                    matched.length > 0 ? [current, ...matched] : [current];

                alreadyPacked = [
                    ...alreadyPacked,
                    ...packedList.map((item) => item.id),
                ];
                remainingActivities = remainingActivities.filter(
                    (activity) =>
                        !packedList.map((item) => item.id).includes(activity.id)
                );
                packedActivites.push(packedList);
            }
        }

        return packedActivites;
    };

    const searchConfig = () => {
        setLoadConfigSettings(true);
    };

    // select new positions
    const onPlaceSelected = (place) => {
        if (place?.geometry) {
            const components = place.address_components || [];
            const findComponent = (type) => {
                const match = components.find((item) => item.types?.includes(type));
                return match ? match.long_name : null;
            };

            placeGeoRef.current = {
                source: "autocomplete",
                regionName: findComponent("administrative_area_level_1"),
                district: findComponent("administrative_area_level_2"),
                city:
                    findComponent("locality") ||
                    findComponent("postal_town") ||
                    findComponent("administrative_area_level_2"),
                postalCode: findComponent("postal_code"),
            };

            setLocationSetManually(true);
            setCurrentPosition({
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
            });
        }
    };

    // select new location whenever the marker is dragged
    const markerDragged = (event) => {
        let newLat = event.latLng.lat();
        let newLng = event.latLng.lng();

        placeGeoRef.current = { source: "drag", lat: newLat, lng: newLng };
        setLocationSetManually(true);

        // handleSearch(null, {
        //     lat: newLat,
        //     lng: newLng,
        // });

        setCurrentPosition({
            lat: newLat,
            lng: newLng,
        });
    };

    const compareAddressLocation = (a, b) => {
        if (
            a?.address?.latitude === b?.address?.latitude &&
            a?.address?.longitude === b?.address?.longitude
        ) {
            return true;
        }

        return false;
    };

    const compareLocation = (a, b) => {
        if (
            a?.address?.latitude === b?.latitude &&
            a?.address?.longitude === b?.longitude
        ) {
            return true;
        }

        return false;
    };

    // selecting the specific event
    const onSelectMarker = (event) => {
        setSelectedAddress({});
        setResponse(null);
        setVisibleEvents(
            foundActivities.filter((events) =>
                compareLocation(events?.[0], event)
            )
        );
        setSelectedAddress(event);
    };

    const activeFilterCount = [
        distance !== 2600,
        Array.isArray(activityTypes) && activityTypes.length > 0,
        date !== moment().format("YYYY-MM-DD"),
        time !== moment().format("HH:00"),
    ].filter(Boolean).length;

    // template
    return (
        <>
            <LoadScript
                googleMapsApiKey={googleMapsAPIKEY}
                libraries={libraries}
                loadingElement={<Loading />}
                preventGoogleFontsLoading={false}
            >
                <GoogleMap
                    mapContainerStyle={mapStyles}
                    zoom={11.8}
                    center={currentPosition}
                    options={defaultOptions}
                    onLoad={() => {
                        setMapLoaded(true);
                    }}
                    onClick={() => {
                        setResponse(null);
                        setSelectedAddress({});
                        setVisibleEvents(foundActivities);
                    }}
                >
                    {selectedAddress?.id ? (
                        <>
                            {selectedAddress.latitude &&
                                currentPosition.lat &&
                                response == null && (
                                    <DirectionsService
                                        options={{
                                            destination: {
                                                lat: parseFloat(
                                                    selectedAddress.latitude
                                                ),
                                                lng: parseFloat(
                                                    selectedAddress.longitude
                                                ),
                                            },
                                            origin: currentPosition,
                                            travelMode: "DRIVING",
                                        }}
                                        callback={(response) => {
                                            setResponse(response);
                                        }}
                                    />
                                )}
                            {response !== null && (
                                <DirectionsRenderer
                                    options={{
                                        directions: response,
                                    }}
                                />
                            )}
                        </>
                    ) : null}

                    {currentPosition?.lat ? (
                        <Marker
                            style={{ zIndex: 10000 }}
                            icon={marker}
                            position={currentPosition}
                            draggable={true}
                            onDragEnd={(event) => markerDragged(event)}
                            title="Aktuálna poloha"
                        />
                    ) : null}

                    {foundActivities.map((activity, idx) => (
                        <React.Fragment
                            key={uuid()} /*key={idx + Math.floor(Math.random() * 30)}*/
                        >
                            {activity.length > 0 && (
                                <Marker
                                    zIndex={
                                        activity.filter(
                                            (item) => item.is_highlighted
                                        ).length > 0 && 9999
                                    }
                                    key={uuid()} /*key={idx + Math.floor(Math.random() * 30)}*/
                                    title={activity?.[0].address?.name}
                                    label={
                                        activity.length > 1
                                            ? {
                                                  text: `${activity?.length}`,
                                                  color:
                                                      activity.filter(
                                                          (item) =>
                                                              item.is_highlighted
                                                      ).length > 0
                                                          ? "black"
                                                          : "white",
                                              }
                                            : ""
                                    }
                                    icon={{
                                        ...markerActivities,
                                        url:
                                            activity?.[0].event_type ===
                                            "future"
                                                ? "images/icons/AP_Map_Pointer_future_terms.svg"
                                                : activity.filter(
                                                      (item) =>
                                                          item.event_type ===
                                                          "solidarity"
                                                  ).length > 0
                                                ? activity.filter(
                                                      (item) =>
                                                          item.is_highlighted
                                                  ).length > 0
                                                    ? "images/icons/Activitypoint_solidarity_map_pointer_special.svg"
                                                    : "images/icons/AP_Map_Pointer_solidarity_terms.svg"
                                                : activity.filter(
                                                      (item) => item.is_paid
                                                  ).length > 0
                                                ? "images/icons/AP_Map_Pointer_business_terms.svg"
                                                : "images/icons/AP_Map_Pointer_ap_inzert.svg",
                                        scaledSize:
                                            activity?.[0].event_type ===
                                            "business"
                                                ? { width: 35, height: 35 }
                                                : { width: 40, height: 40 },
                                        anchor:
                                            activity?.[0].event_type ===
                                            "business"
                                                ? { x: 17.5, y: 17.5 }
                                                : { x: 20.0, y: 20.0 },
                                    }}
                                    clickable={true}
                                    position={{
                                        lat: parseFloat(
                                            activity?.[0].address?.latitude
                                        ),
                                        lng: parseFloat(
                                            activity?.[0].address?.longitude
                                        ),
                                    }}
                                    onClick={() => {
                                        trackingService.trackMapPinClick(activity?.[0]?.activity?.id)
                                        onSelectMarker(activity?.[0].address)
                                    }}
                                >
                                    {selectedAddress.latitude ===
                                        activity?.[0].address?.latitude &&
                                        selectedAddress.longitude ===
                                            activity?.[0].address
                                                ?.longitude && (
                                            <InfoWindow>
                                                <InfoWindowView
                                                    event={activity?.[0]}
                                                    userPosition={
                                                        currentPosition
                                                    }
                                                    mapLoaded={mapLoaded}
                                                />
                                            </InfoWindow>
                                        )}
                                </Marker>
                            )}
                        </React.Fragment>
                    ))}

                    <SearchMapOverlay
                        selectedMarker={selectedAddress}
                        usersPosition={currentPosition}
                        mapLoaded={mapLoaded}
                        autocomplete={onPlaceSelected}
                        searchMyLocation={initCurrentPosition}
                        searchFunc={handleSearch}
                        searchConfig={searchConfig}
                        loadConfigSettings={loadConfigSettings}
                        setLoadConfigSettings={setLoadConfigSettings}
                        handleSearch={handleSearch}
                        searching={searching}
                        foundActivities={visibleEvents}
                        fallbackInfo={fallbackInfo}
                        activeFilterCount={activeFilterCount}
                        config={{
                            distance,
                            date,
                            time,
                            activityTypes,
                        }}
                        setDistanceFunc={setDistance}
                        setDateFunc={setDate}
                        setTimeFunc={setTime}
                        setActivityTypesFunc={setActivityTypes}
                        uuid={uuid}
                    />
                </GoogleMap>
            </LoadScript>
        </>
    );
}

export default Search;
